<template>
  <div>
    <v-divider class="my-6" />

    <div class="mb-4">
      <Typography variant="subtitle-1">Audience</Typography>
      <Typography variant="body-2" class="audience-description">
        Visible to all participants by default.
      </Typography>
    </div>

    <div class="d-flex align-center mb-4">
      <v-switch
        v-model="audienceEnabled"
        color="brandCyan"
        hide-details
        class="mt-0 pt-0 mr-1 shrink-0"
        :disabled="disabled"
        data-testid="audience-toggle"
      />
      <Typography as="span" variant="subtitle-1">Limit to specific groups</Typography>
    </div>

    <Alert
      v-if="audienceEnabled && selectedGroups.length === 0"
      variant="error"
      message="No groups selected — this component won't be visible to anyone."
      class="mb-4"
      data-testid="audience-validation-error"
    />

    <template v-if="audienceEnabled">
      <!-- Selected group chips -->
      <div v-if="selectedGroups.length > 0" class="mb-3" data-testid="selected-chips">
        <v-chip
          v-for="group in selectedGroups"
          :key="group.groupId"
          close
          small
          class="mr-1 mb-1"
          @click:close="removeGroup(group.groupId)"
          :disabled="disabled"
        >
          {{ group.groupDisplayName }}
        </v-chip>
      </div>

      <Typography variant="subtitle-1" class="mb-4">Directory</Typography>

      <v-text-field
        v-model="groupSearch"
        placeholder="Search groups..."
        outlined
        dense
        hide-details
        prepend-inner-icon="mdi-magnify"
        class="mb-3"
        data-testid="group-search"
        :disabled="disabled"
        @input="handleSearchInput"
      />

      <GroupTable
        ref="group-table"
        :selected="selectedGroups"
        :search="groupSearch"
        :includeEveryone="false"
        @select-item="handleSelectItem"
      />
    </template>
  </div>
</template>

<script>
import GroupTable from "@/components/data-table/Groups.vue";
import Typography from "@/components/generic/Typography.vue";
import Alert from "@/components/generic/Alert.vue";

export default {
  name: "AudienceGroupSelector",
  components: { GroupTable, Typography, Alert },
  props: {
    // Retained for API compatibility with the creator components; GroupTable
    // resolves the client from Vuex globalClientId, not this prop.
    clientId: { type: [Number, String], default: null },
    audienceGroups: { type: Array, default: null },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      audienceEnabled: false,
      selectedGroups: [],
      groupSearch: "",
      searchDebounce: null,
    };
  },
  computed: {
    hasValidSelection() {
      return !this.audienceEnabled || this.selectedGroups.length > 0;
    },
  },
  watch: {
    audienceEnabled(val) {
      if (!val) {
        this.selectedGroups = [];
        this.$emit("update:audienceGroups", null);
      } else {
        this.loadGroups();
        this.emitSelection();
      }
    },
  },
  created() {
    this.initFromProp();
  },
  mounted() {
    if (this.audienceEnabled) this.loadGroups();
  },
  methods: {
    initFromProp() {
      if (this.audienceGroups && this.audienceGroups.length > 0) {
        this.audienceEnabled = true;
        this.selectedGroups = this.audienceGroups.map((g) => ({
          groupId: g.groupId,
          groupDisplayName: g.groupDisplayName,
        }));
      }
    },
    loadGroups() {
      this.$nextTick(() => {
        if (this.$refs["group-table"]) {
          this.$refs["group-table"].getGroups(true, "AudienceGroupSelector");
        }
      });
    },
    handleSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.loadGroups();
      }, 300);
    },
    handleSelectItem(item) {
      const groupId = item.groupId;
      const idx = this.selectedGroups.findIndex((g) => g.groupId === groupId);
      if (idx >= 0 && !item.dontRemove) {
        this.selectedGroups.splice(idx, 1);
      } else if (idx === -1) {
        this.selectedGroups.push({
          groupId,
          groupDisplayName: item.groupDisplayName || item.groupName || `Group ${groupId}`,
        });
      }
      this.emitSelection();
    },
    removeGroup(groupId) {
      this.selectedGroups = this.selectedGroups.filter((g) => g.groupId !== groupId);
      this.emitSelection();
    },
    emitSelection() {
      if (this.selectedGroups.length === 0) {
        this.$emit("update:audienceGroups", this.audienceEnabled ? [] : null);
        return;
      }
      this.$emit(
        "update:audienceGroups",
        this.selectedGroups.map((g) => ({
          groupId: g.groupId,
          groupDisplayName: g.groupDisplayName,
        }))
      );
    },
  },
};
</script>

<style scoped>
.audience-description {
  color: rgba(0, 0, 0, 0.6);
}
</style>
